import React, { Suspense } from 'react';

import { Box, GDSBoxProps, GDSStyleObject } from '@leagueplatform/genesis-core';
import { useScrollTopOnLocationChange } from '@leagueplatform/web-common';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import {
  ErrorState,
  LoadingIndicator,
} from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import { useLocation } from 'react-router-dom';
import { getModulePath, LEAGUE_MODULE_NAMES } from '@leagueplatform/core';
import { AppNavBarComponent } from 'components/header-nav/header-nav.component';
import { AppFooter } from 'components/app-footer/app-footer.component';

type BasePageProps = GDSBoxProps<'div'> & {
  css?: GDSStyleObject;
  isPrivatePage?: boolean;
};

export const BasePage: React.FC<BasePageProps> = ({
  children,
  isPrivatePage = true,
  css,
  ...boxProps
}) => {
  useScrollTopOnLocationChange();
  const { formatMessage } = useIntl();
  const { pathname } = useLocation();

  /* GEIS-2132: messaging renders its screens as `position: absolute; inset: 0`
     overlays, so they size to <main> and nothing below it can be reached. The
     footer and its margin come out of that height, which clips the thread list
     and the topic picker on shorter viewports. Figma shows no footer here. */
  const messagingPath = getModulePath(LEAGUE_MODULE_NAMES.messaging);
  const isOverlayRoute =
    pathname === messagingPath || pathname.startsWith(`${messagingPath}/`);
  const showFooter = isPrivatePage && !isOverlayRoute;

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
      }}
    >
      {isPrivatePage && <AppNavBarComponent />}
      <Box
        as="main"
        id="main-content"
        css={{
          position: 'relative',
          flex: '1 0 auto',
          minHeight: isPrivatePage ? 0 : '100vh',
          overflowX: 'hidden',
          ...css,
        }}
        {...boxProps}
      >
        <Suspense fallback={<LoadingIndicator />}>
          <ObservabilityErrorBoundary
            fallback={
              <ErrorState
                headingString={formatMessage({
                  id: 'STR_ERROR_STATE_HEADING_GENERIC',
                })}
                bodyString={formatMessage({ id: 'STR_ERROR_STATE_BODY_API' })}
                buttonText={formatMessage({ id: 'STR_RELOAD' })}
                error={null}
              />
            }
            errorContext={{
              context: {
                Context: { location: 'Base Page ErrorBoundary' },
              },
            }}
          >
            {children}
          </ObservabilityErrorBoundary>
        </Suspense>
      </Box>
      {showFooter && <AppFooter />}
    </Box>
  );
};
